/**
 * Greenlight Engine — Frontend service.
 *
 * Cloud users: calls backend → Claude API (1895's key)
 * Vault users: calls Claude directly from browser (user's key)
 */
import axios from 'axios';
import { isClaudeByokActive, claudeJson } from './claudeByokService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Cloud mode (backend-mediated) ──────────────────────────

/** Launch a Greenlight analysis via backend */
export async function runGreenlightAnalysis(projectId, scriptContent, title) {
  if (isClaudeByokActive()) {
    return await runGreenlightByok(projectId, scriptContent, title);
  }

  const { data } = await axios.post(`${API}/greenlight/analyze`, {
    project_id: projectId,
    script_content: scriptContent,
    title,
  });
  return data;
}

/** Poll analysis progress */
export async function getGreenlightStatus(analysisId) {
  const { data } = await axios.get(`${API}/greenlight/analysis/${analysisId}`);
  return data;
}

/** Get latest analysis for a project */
export async function getProjectGreenlight(projectId) {
  const { data } = await axios.get(`${API}/greenlight/project/${projectId}`);
  return data;
}

/** Get analysis version history */
export async function getGreenlightHistory(projectId) {
  const { data } = await axios.get(`${API}/greenlight/project/${projectId}/history`);
  return data;
}

/** Fill only missing/empty analysis steps */
export async function fillMissingAnalysis(analysisId) {
  const { data } = await axios.post(`${API}/greenlight/analyze/${analysisId}/fill-missing`);
  return data;
}

// ── BYOK mode (browser-side) ──────────────────────────────

const GREENLIGHT_WEIGHTS = {
  writing_craft: 0.20,
  character_arc: 0.15,
  emotional_architecture: 0.15,
  narrative_logic: 0.15,
  commercial_viability: 0.10,
  audience_breadth: 0.10,
  producibility: 0.10,
  originality: 0.05,
};

function getBadge(score) {
  if (score >= 90) return { label: '1895 GREENLIT — MASTERWORK', tier: 'gold' };
  if (score >= 80) return { label: '1895 GREENLIT — STRONG', tier: 'silver' };
  if (score >= 70) return { label: '1895 NOTED — PROMISING', tier: 'bronze' };
  if (score >= 50) return { label: 'IN DEVELOPMENT', tier: null };
  return { label: 'NEEDS REWRITE', tier: null };
}

/**
 * Full Greenlight analysis running entirely in the browser via BYOK.
 * Returns a simulated analysis object matching the backend format.
 */
async function runGreenlightByok(projectId, scriptContent, title) {
  // We'll return a streaming-style object the UI can poll
  const analysisId = `byok-${Date.now()}`;

  // Store in-progress state
  const state = {
    id: analysisId,
    project_id: projectId,
    title,
    status: 'processing',
    progress: 0,
    current_step: 'Starting analysis...',
    source: 'byok',
  };

  // Store state so polling can access it
  _byokAnalyses.set(analysisId, state);

  // Run pipeline async
  _runByokPipeline(analysisId, projectId, scriptContent, title).catch((err) => {
    const s = _byokAnalyses.get(analysisId);
    if (s) {
      s.status = 'failed';
      s.error = err.message;
    }
  });

  return { analysis_id: analysisId, status: 'processing' };
}

// In-memory store for BYOK analyses
const _byokAnalyses = new Map();

/** Get BYOK analysis status (used by polling) */
export function getByokAnalysisStatus(analysisId) {
  return _byokAnalyses.get(analysisId) || null;
}

async function _runByokPipeline(analysisId, projectId, scriptContent, title) {
  const state = _byokAnalyses.get(analysisId);
  const update = (step, progress, extra = {}) => {
    Object.assign(state, { current_step: step, progress, ...extra });
  };

  // Step 1: Writing Craft
  update('Analyzing writing craft...', 10);
  const writing = await claudeJson(
    'You are a senior development executive with 20+ years of script evaluation experience.',
    _writingCraftPrompt(scriptContent),
    { maxTokens: 8192 }
  );
  update('Writing craft complete', 25, { writing_craft: writing });

  // Step 2: Characters
  update('Analyzing characters...', 30);
  const characters = await claudeJson(
    'You are an expert character analyst specializing in screenplay evaluation.',
    _characterPrompt(scriptContent),
    { maxTokens: 12288 }
  );
  update('Characters analyzed', 45, { characters });

  // Step 3: Emotional Architecture
  update('Mapping emotional architecture...', 50);
  const emotions = await claudeJson(
    'You are the Head of Development at a major studio with extraordinary ability to predict emotional responses.',
    _emotionPrompt(scriptContent),
    { maxTokens: 16384, temperature: 0.5 }
  );
  update('Emotions mapped', 65, { emotional_architecture: emotions });

  // Step 4: Logic
  update('Checking narrative logic...', 70);
  const logic = await claudeJson(
    'You are an expert story editor specializing in finding logical inconsistencies.',
    _logicPrompt(scriptContent),
    { maxTokens: 8192 }
  );
  update('Logic analysis complete', 80, { narrative_logic: logic });

  // Step 5: Commercial
  update('Evaluating commercial potential...', 70);
  const commercial = await claudeJson(
    'You are a film industry business analyst and market researcher.',
    _commercialPrompt(scriptContent),
    { maxTokens: 4096 }
  );
  update('Commercial analysis complete', 75, { commercial });

  // Step 6: Character Arcs
  update('Tracking character arcs...', 78);
  const characterArcs = await claudeJson(
    'You are an expert in dramatic structure and character transformation.',
    _characterArcPrompt(scriptContent, characters),
    { maxTokens: 12288 }
  );
  update('Character arcs mapped', 82, { character_arcs: characterArcs });

  // Step 7: Budget & Risk
  update('Analyzing budget & risk...', 84);
  const budgetRisk = await claudeJson(
    'You are an experienced line producer and unit production manager with 15+ years in film production.',
    _budgetRiskPrompt(scriptContent),
    { maxTokens: 16384, temperature: 0.3 }
  );
  update('Budget & risk analyzed', 88, {
    budget: budgetRisk.budget || {},
    risk: budgetRisk.risk || {},
  });

  // Step 8: Dead Zones
  update('Detecting dead zones...', 86);
  const deadZones = await claudeJson(
    'You are a script doctor and pacing specialist.',
    _deadZonePrompt(scriptContent, emotions),
    { maxTokens: 4096 }
  );
  update('Dead zones detected', 88, { dead_zones: deadZones });

  // Step 9: Audience Segmentation
  update('Segmenting audience...', 90);
  const audienceSegmentation = await claudeJson(
    'You are a film marketing strategist and audience research expert.',
    _audienceSegmentationPrompt(scriptContent, commercial),
    { maxTokens: 4096 }
  );
  update('Audience segmented', 92, { audience_segmentation: audienceSegmentation });

  // Step 10: Comparable Films
  update('Finding comparable films...', 93);
  const comparableFilms = await claudeJson(
    'You are a film industry analyst with encyclopedic knowledge of box office data.',
    _comparableFilmsPrompt(scriptContent, commercial),
    { maxTokens: 6144 }
  );
  update('Comparable films identified', 95, { comparable_films: comparableFilms });

  // Step 11: Compute score
  const subScores = {
    writing_craft: writing.overall_score || 50,
    character_arc: characters.ensemble_score || 50,
    emotional_architecture: emotions.flow_metrics?.pacing_score || 50,
    narrative_logic: logic.logic_score || 50,
    commercial_viability: commercial.commercial_viability?.score || 50,
    audience_breadth: commercial.audience_breadth?.score || 50,
    producibility: commercial.producibility?.score || 50,
    originality: commercial.originality?.score || 50,
  };

  const greenlightScore = Math.round(
    Object.keys(GREENLIGHT_WEIGHTS).reduce(
      (sum, k) => sum + (subScores[k] || 50) * GREENLIGHT_WEIGHTS[k], 0
    ) * 10
  ) / 10;

  const badge = getBadge(greenlightScore);

  // Step 12: Executive summary
  update('Generating executive summary...', 97);
  const summary = await claudeJson(
    'You are a senior studio executive writing a development coverage report.',
    _summaryPrompt(greenlightScore, badge, writing, characters, emotions, logic, commercial, title),
    { maxTokens: 2048 }
  );

  // Done
  Object.assign(state, {
    status: 'completed',
    progress: 100,
    current_step: 'Analysis complete',
    greenlight_score: greenlightScore,
    badge,
    sub_scores: subScores,
    executive_summary: summary,
    completed_at: new Date().toISOString(),
  });
}

// ── Prompt Builders ────────────────────────────────────────

function _writingCraftPrompt(script) {
  return `Analyze this screenplay's writing craft. Score each of 10 categories 0-100.

Categories: Authenticity, Originality, Voice Consistency, Subtext Density, Economy, Tonal Control, Sensory Immersion, Emotional Honesty, Wit & Intelligence, Rhythm & Pacing.

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"overall_score":<0-100>,"categories":[{"name":"<name>","score":<0-100>,"assessment":"<paragraph>","strengths":[{"line":"<quote>","explanation":"<why>"}],"weaknesses":[{"line":"<quote>","explanation":"<why>"}]}],"strongest_pages_description":"<text>","weakest_pages_description":"<text>"}`;
}

function _characterPrompt(script) {
  return `Analyze every named character. Score 10 dimensions 0-100: Dimensionality, Motivation Clarity, Distinctiveness, Fascination, Dialogue Fingerprint, Agency, Backstory Integration, Relationship Chemistry, Audience Proxy, Castability.

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"characters":[{"name":"<NAME>","one_line_description":"<desc>","archetype":"<type>","estimated_line_count":<n>,"estimated_scene_count":<n>,"scores":{"dimensionality":<n>,"motivation_clarity":<n>,"distinctiveness":<n>,"fascination":<n>,"dialogue_fingerprint":<n>,"agency":<n>,"backstory_integration":<n>,"relationship_chemistry":<n>,"audience_proxy":<n>,"castability":<n>},"overall_score":<0-100>,"assessment":"<text>","notable_lines":["<line>"],"relationships":[{"character":"<name>","type":"conflict|alliance|romance|mentor|mystery|family","intensity":<1-10>,"description":"<text>"}]}],"ensemble_score":<0-100>,"protagonist":"<name>","antagonist":"<name or null>","character_balance_assessment":"<text>"}`;
}

function _emotionPrompt(script) {
  return `Analyze emotional content per scene. Score 12 emotions 0-100: joy, love, humor, wonder, tension, fear, sadness, anger, disgust, confusion, nostalgia, catharsis.

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"scenes":[{"scene_number":<n>,"slug_line":"<heading>","emotions":{"joy":<n>,"love":<n>,"humor":<n>,"wonder":<n>,"tension":<n>,"fear":<n>,"sadness":<n>,"anger":<n>,"disgust":<n>,"confusion":<n>,"nostalgia":<n>,"catharsis":<n>},"primary_emotion":"<name>","intensity_composite":<0-100>,"emotional_summary":"<sentence>","is_dead_zone":false}],"flow_metrics":{"emotional_range":<n>,"peak_count":<n>,"valley_count":<n>,"pacing_score":<0-100>,"opening_hook_score":<0-100>,"closing_resonance_score":<0-100>},"dead_zones":[{"scene_range":"<range>","type":"emotional_flatline|redundant_exposition|stalled_plot|tonal_monotone","description":"<text>","suggestions":["<fix>"]}]}`;
}

function _logicPrompt(script) {
  return `Analyze for logical consistency, plot holes, and structural integrity.

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"logic_score":<0-100>,"issues":[{"type":"continuity|unresolved_thread|knowledge_violation|timeline|motivation|deus_ex_machina|stakes_deflation|world_rule","severity":"critical|moderate|minor","description":"<text>","location":"<scene>","suggested_fix":"<text>"}],"plot_threads":[{"thread":"<desc>","introduced":"<where>","resolved":true,"resolution_location":"<where>"}],"structural_assessment":"<paragraph>"}`;
}

function _commercialPrompt(script) {
  return `Analyze commercial potential, audience appeal, producibility, and originality.

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"commercial_viability":{"score":<0-100>,"market_potential":"low|medium|high|blockbuster","strengths":["<text>"],"weaknesses":["<text>"],"comparable_films":["<film (year)>"]},"audience_breadth":{"score":<0-100>,"age_appeal":{"13_17":<n>,"18_24":<n>,"25_34":<n>,"35_44":<n>,"45_54":<n>,"55_plus":<n>},"gender_appeal":{"male":<n>,"female":<n>,"male_reasoning":"<text>","female_reasoning":"<text>"},"social_context":{"solo":<n>,"date_night":<n>,"friends":<n>,"family_kids":<n>,"family_adults":<n>}},"producibility":{"score":<0-100>,"budget_tier":"micro|low|mid|high|tentpole","estimated_shoot_days":<n>,"key_challenges":["<text>"],"cost_reduction_opportunities":["<text>"]},"originality":{"score":<0-100>,"fresh_elements":["<text>"],"derivative_elements":["<text>"],"market_gap":"<text>"}}`;
}

function _characterArcPrompt(script, characters) {
  const charNames = (characters.characters || []).slice(0, 6).map(c => c.name).join(', ');
  return `Analyze character arcs in this screenplay. Focus on: ${charNames}.

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"arcs":[{"character":"<name>","arc_type":"transformation|flat|negative|circular","transformation_magnitude":<0-100>,"transformation_believability":<0-100>,"arc_completeness":<0-100>,"thematic_resonance":<0-100>,"plot_necessity":<0-100>,"emotional_payoff":<0-100>,"overall_arc_score":<0-100>,"trajectory":[{"scene":<n>,"state":<0-100>,"label":"<inflection point>","description":"<what happens>"}],"assessment":"<text>","red_flags":["<problem>"]}],"act_structure":{"act1_end":<n>,"act2a_end":<n>,"act2b_end":<n>,"act3_start":<n>},"overall_arc_score":<0-100>,"thematic_coherence":"<paragraph>"}`;
}

function _budgetRiskPrompt(script) {
  return `Analyze each scene for production budget and risk factors.

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"budget":{"scenes":[{"scene_number":<n>,"slug_line":"<heading>","page_range":[<start>,<end>],"cost_tier":"low|moderate|high|very_high","relative_cost":<0-100>,"shooting_days":<n>,"cost_drivers":[{"category":"location_complexity|vfx|stunts|crowd_extras|special_equipment|night_shoot|wardrobe_makeup|animals_children|post_production","score":<0-100>,"note":"<text>"}],"cost_reduction_suggestion":"<text>"}],"summary":{"estimated_shooting_days":<n>,"budget_tier":"Micro (<$1M)|Low ($1-5M)|Mid ($5-20M)|High ($20-50M)|Tentpole ($50M+)","top_5_expensive_scenes":[<scene numbers>],"budget_concentration":<percentage>,"cost_reduction_opportunities":[{"scene":<n>,"suggestion":"<text>","savings_percentage":<n>}]},"overall_producibility_score":<0-100>},"risk":{"scenes":[{"scene_number":<n>,"slug_line":"<heading>","risk_level":"critical|high|medium|low","risk_score":<0-100>,"risk_factors":[{"type":"weather_dependency|technical_complexity|vfx_uncertainty|schedule_fragility|location_risk|talent_bottleneck|safety_concerns|regulatory_legal|child_animal|reshoot_probability","severity":<0-100>,"description":"<text>","mitigation":"<text>"}],"contingency":"<text>"}],"summary":{"critical_count":<n>,"high_count":<n>,"medium_count":<n>,"low_count":<n>,"overall_risk_score":<0-100>,"top_risk_categories":[{"category":"<type>","scene_count":<n>,"avg_severity":<0-100>}],"risk_assessment":"<paragraph>"}}}`;
}

function _deadZonePrompt(script, emotions) {
  const existingDeadZones = JSON.stringify(emotions.dead_zones || []);
  const pacingScore = emotions.flow_metrics?.pacing_score || 'N/A';
  return `Analyze this screenplay for dead zones — boring, redundant, or unnecessary stretches.

Emotional analysis found these potential dead zones: ${existingDeadZones}
Pacing score: ${pacingScore}/100

Types: emotional_flatline, redundant_exposition, stalled_plot, character_holding, dialogue_spinning, tonal_monotone, missing_stakes.

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"dead_zones":[{"type":"<type>","page_start":<n>,"page_end":<n>,"scene_start":<n>,"scene_end":<n>,"severity":"critical|moderate|minor","description":"<text>","suggestions":["<fix1>","<fix2>","<fix3>"],"affected_characters":["<name>"]}],"total_dead_pages":<n>,"dead_zone_percentage":<n>,"pacing_score":<0-100>,"pacing_assessment":"<paragraph>"}`;
}

function _audienceSegmentationPrompt(script, commercial) {
  const existing = JSON.stringify(commercial.audience_breadth || {});
  return `Perform detailed audience segmentation analysis for this screenplay.

Existing audience data: ${existing}

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"overall_appeal_score":<0-100>,"primary_audience":"<description>","secondary_audience":"<description>","geography_appeal":{"north_america":<0-100>,"europe":<0-100>,"latin_america":<0-100>,"middle_east_north_africa":<0-100>,"sub_saharan_africa":<0-100>,"south_asia":<0-100>,"east_asia":<0-100>,"southeast_asia":<0-100>,"oceania":<0-100>},"income_appeal":{"budget_conscious":<0-100>,"middle_income":<0-100>,"affluent":<0-100>,"ultra_premium":<0-100>},"marketing_insights":"<paragraph>","distribution_recommendation":"<paragraph>"}`;
}

function _comparableFilmsPrompt(script, commercial) {
  const existing = JSON.stringify(commercial.commercial_viability?.comparable_films || []);
  return `Identify the 5-8 most comparable films to this screenplay with market data.

Previously identified comps: ${existing}

FULL SCRIPT:
${script.substring(0, 180000)}

Return ONLY valid JSON:
{"comparable_films":[{"title":"<Film (Year)>","similarity_score":<0-100>,"shared_elements":["<element>"],"differentiators":["<diff>"],"box_office":{"domestic":"<$XXM>","worldwide":"<$XXM>","budget":"<$XXM>","roi":"<percentage>"},"audience_ratings":{"rotten_tomatoes":<0-100>,"imdb":<0-10>,"metacritic":<0-100>},"streaming_performance":"<note>","relevance_note":"<sentence>"}],"market_gap":{"description":"<niche>","opportunity_score":<0-100>,"timing_assessment":"<favorable|neutral|unfavorable — explanation>","competitive_landscape":["<project>"]},"positioning_summary":"<paragraph>"}`;
}

function _summaryPrompt(score, badge, writing, characters, emotions, logic, commercial, title) {
  return `Write an executive summary for "${title}".

Greenlight Score: ${score}/100 — ${badge.label}
Writing Craft: ${writing.overall_score}/100
Ensemble: ${characters.ensemble_score}/100
Pacing: ${emotions.flow_metrics?.pacing_score}/100
Logic: ${logic.logic_score}/100
Commercial: ${commercial.commercial_viability?.score}/100
Audience: ${commercial.audience_breadth?.score}/100
Producibility: ${commercial.producibility?.score}/100
Originality: ${commercial.originality?.score}/100

Return ONLY valid JSON:
{"one_line_verdict":"<sentence>","synopsis":"<3-4 sentences>","top_strengths":["<s1>","<s2>","<s3>"],"top_improvements":["<i1>","<i2>","<i3>"],"development_notes":"<2-3 paragraphs>","market_positioning":"<paragraph>"}`;
}
